/** tm/components/ReportMemoBar.tsx — 상담 메모 업로드 + 01·02·03 자동 채우기 + 리포트 자동 발행
 *
 *  결과입력 모달 맨 위에 놓인다. 상담 중 HWP 로 적은 메모를 PDF 로 저장해 올리면
 *  ① 리포트 뒤에 간지와 함께 원본이 병합되고
 *  ② 그 내용을 읽어 01 회사현황 · 02 상담내용 · 03 해야할 일을 자동으로 채우고
 *  ③ 이어서 리포트를 바로 만들어 미리보기를 띄운다 (2026-08-17 사용자 요청 — 버튼 누르지 않아도 되게)
 *  채워진 내용은 전부 사람이 고칠 수 있다 (AI 는 초안까지만).
 *
 *  ③ 을 위해 형제 컴포넌트인 ReportSection 에 초안을 그대로 넘겨야 한다.
 *  둘은 부모(ConsultResultModal)를 통해서만 이어져 있고, 부모의 상태 갱신은 비동기라
 *  방금 채운 값을 곧바로 생성에 쓸 수 없다. 그래서 초안을 실은 window 이벤트로 직접 전달한다.
 */
import { useState, useEffect, useCallback } from 'react';
import { C, T, R } from './ui';
import * as rapi from '../api-report';
import type { ReportTodo } from '../api-report';
import { useFileDrop } from '../useFileDrop';

/** ReportSection 이 듣는 이벤트 이름 — 두 파일에서만 쓴다 */
export const DRAFT_READY = 'roum:report-draft-ready';

export interface DraftReadyDetail {
  consultId: string;
  company: string;
  consult: string;
  todos: ReportTodo[];
}

interface Props {
  consultId: string;
  /** 불러온 초안을 상위 입력칸에 반영 */
  onApplyDraft: (company: string, consult: string, todos: ReportTodo[]) => void;
  onToast: (msg: string, type?: string) => void;
}

export default function ReportMemoBar({ consultId, onApplyDraft, onToast }: Props) {
  const [name, setName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [aiErr, setAiErr] = useState('');   // 메모 읽기 실패 사유 — 토스트는 잠깐이라 막대 안에 남긴다(2026-10-13 · 요금 한도)

  // 모달을 열 때 서버에 저장된 01·02·03 을 입력칸에 되살린다.
  // 리포트 발행 시점에 서버가 content_company/content_consult 를 기록하므로,
  // 이걸 안 채우면 재진입 화면에서 리포트에는 내용이 있는데 입력칸만 비어 보이고
  // 그 상태로 [저장]을 누르면 원문이 빈 값으로 덮인다.
  const load = useCallback(async () => {
    try {
      const s = await rapi.getReport(consultId);
      setName(s.memo_pdf_name || '');
      const co = s.content_company || '';
      const cs = s.content_consult || '';
      const td = Array.isArray(s.report?.todos) ? (s.report?.todos as ReportTodo[]) : [];
      if (co || cs || td.length) onApplyDraft(co, cs, td);
    } catch { /* 아직 저장 안 된 상담 */ }
    // onApplyDraft 는 상위에서 매 렌더 새로 만들어질 수 있어 의존성에서 뺀다 — 상담이 바뀔 때만 다시 읽는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultId]);
  useEffect(() => { load(); }, [load]);

  async function upload(file: File) {
    if (!/\.pdf$/i.test(file.name)) {
      onToast('PDF 파일만 올릴 수 있습니다 — 한글에서 "PDF로 저장" 후 올려주세요', 'error');
      return;
    }
    setUploading(true);
    try {
      const r = await rapi.uploadMemo(consultId, file);
      setName(r.memo_pdf_name);
      onToast('상담 메모 등록됨 — 내용을 읽어 리포트까지 만듭니다');
      await extract();
    } catch (e) { onToast(e instanceof Error ? e.message : '업로드 실패', 'error'); }
    finally { setUploading(false); }
  }

  // 끌어다 놓기(2026-10-29) — 막대 어디에 놓아도 [PDF 올리기]와 같음 · 올리는 · 읽는 중엔 받지 않음
  const drop = useFileDrop(f => { void upload(f); }, uploading || extracting);

  async function extract() {
    setExtracting(true);
    try {
      const d = await rapi.extractDraft(consultId);
      if (d.aiError) { setAiErr(d.aiError); onToast(`초안 생성 실패 — ${d.aiError}`, 'error'); return; }
      setAiErr('');
      const company = d.company || '';
      const consult = d.consult || '';
      const todos = Array.isArray(d.todos) ? d.todos : [];
      onApplyDraft(company, consult, todos);
      const n01 = company.split('\n').filter(Boolean).length;
      const n02 = consult.split('\n').filter(Boolean).length;
      onToast(`메모에서 채웠습니다 · 01 ${n01}줄 / 02 ${n02}줄 / 03 ${todos.length}행 — 이어서 리포트를 만듭니다`);
      // 방금 뽑은 초안을 그대로 실어 보낸다. 부모 상태를 기다리지 않으므로 값이 어긋나지 않는다.
      const detail: DraftReadyDetail = { consultId, company, consult, todos };
      window.dispatchEvent(new CustomEvent<DraftReadyDetail>(DRAFT_READY, { detail }));
    } catch (e) { onToast(e instanceof Error ? e.message : '불러오기 실패', 'error'); }
    finally { setExtracting(false); }
  }

  const btn = (primary?: boolean) => ({
    padding: '7px 14px', borderRadius: R.sm, fontSize: T.xs, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${C.accent}`, background: primary ? C.accent : C.white, color: primary ? '#fff' : C.accent,
    flexShrink: 0, display: 'inline-block', whiteSpace: 'nowrap' as const,
  });

  return (
    <div {...drop.bind} style={{ marginBottom: 20, padding: 12, borderRadius: R.sm, border: `1px ${drop.over ? 'dashed' : 'solid'} ${drop.over ? C.accent : C.border}`, background: drop.over ? C.accentLt : C.bgAlt, transition: 'background .12s, border-color .12s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: T.sm, fontWeight: 700, flexShrink: 0 }}>📎 상담 메모</span>
        <span style={{ flex: 1, minWidth: 120, fontSize: T.xs, color: drop.over ? C.accent : name ? C.text : C.muted, fontWeight: drop.over ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {drop.over ? '여기에 놓으면 올라가요' : name || '상담 중 작성한 메모 PDF를 끌어다 놓거나 올려주세요'}
        </span>
        <label style={btn(!name)}>
          {uploading ? '올리는 중…' : name ? '교체' : 'PDF 올리기'}
          <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </label>
        {name && (
          <button onClick={extract} disabled={extracting} style={btn(true)}>
            {extracting ? '읽는 중…' : '✨ 다시 읽어 새로 만들기'}
          </button>
        )}
      </div>
      {aiErr && (
        <div role="alert" style={{ marginTop: 8, padding: '8px 10px', borderRadius: R.sm, background: '#FEF2F2', border: `1px solid ${C.red}`, color: C.red, fontSize: T.xs, fontWeight: 600, lineHeight: 1.5 }}>
          메모를 읽지 못했어요 — {aiErr}
        </div>
      )}
      <div style={{ fontSize: 10, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
        올린 메모는 리포트 뒤에 간지와 함께 원본 그대로 붙습니다.
        {name
          ? ' 올리면 아래 01·02·03이 채워지고 리포트까지 자동으로 만들어집니다 — 내용을 고친 뒤에는 [🔄 다시 만들기]를 눌러주세요.'
          : ' 한글에서 "PDF로 저장" 후 올려주세요.'}
      </div>
    </div>
  );
}
